import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  createIOMonitoringByAppointmentId,
  deleteIOMonitoringByAppointmentId,
  getIOMonitoringByAppointmentId,
  updateIOMonitoringByAppointmentId,
} from "../../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../../util/AppColors";
import "./IOMonitoring.css";
import {
  bloodProductTypeOptions,
  fecalScoreOptions,
  hydrationOptions,
  initialVitals,
  intakeCategoryOptions,
  logTypeOptions,
  mentationOptions,
  nursingCategoryOptions,
  nursingFindingByCategory,
  outputCategoryOptions,
} from "./mockIOMonitoringData";

const defMandateObj = { clinic: {}, vets: [] };
const delBtn = { color: AppColors.red, width: 20, height: 20 };
const edtBtn = { color: AppColors.appPrimary, width: 20, height: 20 };

const getInitLogForm = () => ({
  type: "Intake",
  category: "IV Fluid",
  // Intake fields
  fluidType: "",
  rate: "",
  volumeInfused: "",
  offeredMl: "",
  consumedMl: "",
  dietFoodType: "",
  offeredG: "",
  consumedG: "",
  productType: "Whole Blood",
  volumeMl: "",
  // Output fields
  volume: "",
  colourCharacter: "",
  episodes: "1",
  character: "",
  fecalScore: "3 — Firm, moist, holds shape",
  notes: "",
  description: "",
  estimatedVolume: "",
  sourceSite: "",
  // Nursing fields
  site: "",
  finding: "Patent, no issues",
});

const getDefaultCategory = (type) => {
  if (type === "Output") return "Urine";
  if (type === "Nursing Check") return "IV Catheter / Site Check";
  return "IV Fluid";
};

const getDefaultFinding = (category) =>
  nursingFindingByCategory[category]?.[0]?.value || "";

const getCategoryOptions = (type) => {
  if (type === "Output") return outputCategoryOptions;
  if (type === "Nursing Check") return nursingCategoryOptions;
  return intakeCategoryOptions;
};

const getBadgeType = (type, category = "") => {
  if (type === "Output") return "warning";
  if (type === "Nursing Check") return "purple";
  if (type === "Intake") return "info";

  const cat = String(category).toLowerCase();
  if (
    cat.includes("urine") ||
    cat.includes("vomiting") ||
    cat.includes("feces") ||
    cat.includes("blood loss") ||
    cat.includes("wound drainage") ||
    cat.startsWith("other (")
  ) {
    return "warning";
  }
  if (
    cat.includes("catheter") ||
    cat.includes("wound") ||
    cat.includes("incision") ||
    cat.includes("pressure") ||
    cat.includes("nursing")
  ) {
    return "purple";
  }
  return "info";
};

const buildLogDescription = (form) => {
  const category = String(form?.category || "")
    .toLowerCase()
    .trim();

  // Intake
  if (category === "iv fluid") {
    if (!form?.fluidType && !form?.rate && !form?.volumeInfused) {
      return "— running at ml/hr, ml infused so far";
    }
    return `${form.fluidType || "—"} — running at ${form.rate || ""} ml/hr, ${form.volumeInfused || ""} ml infused since start`;
  }
  if (category === "oral water") {
    return `Offered ${form.offeredMl || "—"} ml, consumed ${form.consumedMl || "—"} ml`;
  }
  if (category === "food") {
    return `${form.dietFoodType || "Food"} — offered ${form.offeredG || "—"} g, consumed ${form.consumedG || "—"}`;
  }
  if (category === "blood / blood products") {
    return `${form.productType || "Blood product"} — ${form.volumeMl || "—"} ml at ${form.rate || "—"} ml/hr`;
  }

  // Output
  if (category === "urine") {
    return `${form.volume ? `${form.volume} ml` : "—"}, ${form.colourCharacter || "—"}`;
  }
  if (category === "vomiting") {
    const episodes = form.episodes || "1";
    const epLabel = String(episodes) === "1" ? "episode" : "episodes";
    return `${episodes} ${epLabel}, ${form.character || "—"}`;
  }
  if (category === "feces") {
    return `${form.fecalScore || "—"}, ${form.notes || ""}`.trim();
  }
  if (category === "blood loss / hemorrhage") {
    return `${form.estimatedVolume || "—"}, source: ${form.sourceSite || "—"}`;
  }
  if (category.startsWith("other (")) {
    return form.description || "—";
  }

  // Nursing
  if (category === "other nursing note") {
    return form.description || "—";
  }
  if (category === "pressure area check") {
    return form.finding || "—";
  }
  if (
    category.includes("catheter") ||
    category.includes("wound") ||
    category.includes("incision")
  ) {
    return `${form.site || "—"} — ${form.finding || "—"}`;
  }

  return form.description || "—";
};

const formatLogTime = (item) => {
  const raw =
    item?.createdDate ||
    item?.createdAt ||
    item?.loggedAt ||
    item?.time ||
    item?.updatedDate;
  if (!raw) return { display: "—", hourMin: "—", ampm: "" };
  const m = moment(raw);
  if (m.isValid()) {
    return {
      display: m.format("hh:mm a"),
      hourMin: m.format("hh:mm"),
      ampm: m.format("a"),
    };
  }
  return { display: String(raw), hourMin: String(raw), ampm: "" };
};

const getUserNameById = (id, clinicVets, clinicDet, profile) => {
  if (!id) return "";
  const vetMatch = clinicVets?.find((vet) => vet?.doctorId === id);
  if (vetMatch?.name) return vetMatch.name;
  if (clinicDet?.id === id && clinicDet?.name) return clinicDet.name;
  if (profile?.id === id && profile?.name) return profile.name;
  return "";
};

const getLoggedByLabel = (item, clinicVets, clinicDet, profile) => {
  if (item?.loggedBy) return item.loggedBy;

  const nameFromId = getUserNameById(
    item?.createdBy,
    clinicVets,
    clinicDet,
    profile,
  );
  if (nameFromId) return `Logged by ${nameFromId}`;

  if (item?.createdByName) return `Logged by ${item.createdByName}`;
  return "Logged by ";
};

const canManageIoEntry = (item, user, inpatientDoctorId) => {
  if (!user?.id || !item?.ioMonitoringId) return false;
  const userId = String(user.id);
  if (inpatientDoctorId != null && String(inpatientDoctorId) === userId) {
    return true;
  }
  return item?.createdBy != null && String(item.createdBy) === userId;
};

const mapIoTimelineEntry = (item, clinicVets, clinicDet, profile) => {
  const timeInfo = formatLogTime(item);
  const ioMonitoringId = item?.ioMonitoringId ?? item?.id ?? null;
  return {
    ...item,
    ioMonitoringId,
    id: ioMonitoringId ?? `${item?.type}-${item?.category}-${item?.createdAt}`,
    time: timeInfo.display,
    timeHourMin: timeInfo.hourMin,
    timeAmPm: timeInfo.ampm,
    category: (item?.category || "").toUpperCase(),
    description:
      // (item?.description && String(item.description).trim()) ||
      buildLogDescription(item),
    loggedBy: getLoggedByLabel(item, clinicVets, clinicDet, profile),
    badgeType: getBadgeType(item?.type, item?.category),
  };
};

const mapIoItemToForm = (item = {}) => {
  const type = item?.type || "Intake";
  const category = item?.category || getDefaultCategory(type);
  return {
    ...getInitLogForm(),
    type,
    category,
    fluidType: item?.fluidType ?? "",
    rate: item?.rate ?? "",
    volumeInfused: item?.volumeInfused ?? "",
    offeredMl: item?.offeredMl ?? "",
    consumedMl: item?.consumedMl ?? "",
    dietFoodType: item?.dietFoodType ?? "",
    offeredG: item?.offeredG ?? "",
    consumedG: item?.consumedG ?? "",
    productType: item?.productType ?? "Whole Blood",
    volumeMl: item?.volumeMl ?? "",
    volume: item?.volume ?? "",
    colourCharacter: item?.colourCharacter ?? "",
    episodes: item?.episodes ?? "1",
    character: item?.character ?? "",
    fecalScore: item?.fecalScore ?? "3 — Firm, moist, holds shape",
    notes: item?.notes ?? "",
    description: item?.description ?? "",
    estimatedVolume: item?.estimatedVolume ?? "",
    sourceSite: item?.sourceSite ?? "",
    site: item?.site ?? "",
    finding: item?.finding || getDefaultFinding(category),
  };
};

const buildIoRequestPayload = (form) => ({
  type: form.type || "",
  category: form.category || "",
  description: form.description || buildLogDescription(form) || "",
  fluidType: form.fluidType || "",
  rate: form.rate || "",
  volumeInfused: form.volumeInfused || "",
  offeredMl: form.offeredMl || "",
  consumedMl: form.consumedMl || "",
  dietFoodType: form.dietFoodType || "",
  offeredG: form.offeredG || "",
  consumedG: form.consumedG || "",
  productType: form.productType || "",
  volumeMl: form.volumeMl || "",
  volume: form.volume || "",
  colourCharacter: form.colourCharacter || "",
  episodes: form.episodes || "",
  character: form.character || "",
  fecalScore: form.fecalScore || "",
  notes: form.notes || "",
  estimatedVolume: form.estimatedVolume || "",
  sourceSite: form.sourceSite || "",
  site: form.site || "",
  finding: form.finding || "",
});

const IOMonitoring = () => {
  const dispatch = useDispatch();
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const apntDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const { ioMonitoringList } = useSelector((state) => state?.clinic);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinic: clinicDet, vets: clinicVets } = useMemo(() => {
    if (!mandateDataRaw) return defMandateObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
      };
    } catch {
      return defMandateObj;
    }
  }, [mandateDataRaw]);
  const profile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const appointmentId =
    petAndApntData?.appointment?.appointmentId ??
    apntDet?.appointment?.appointmentId;
  const inpatientDoctorId = petAndApntData?.inpatientResponse?.doctorId;

  const [vitals, setVitals] = useState(initialVitals);

  const [showVitalsModal, setShowVitalsModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [delModVsble, setDelModVsble] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [showNoFieldModal, setShowNoFieldModal] = useState(false);
  const [activeModalField, setActiveModalField] = useState(null);
  const [isListening, setIsListening] = useState(false);

  const [vitalsForm, setVitalsForm] = useState({
    temp: "",
    pulse: "",
    resp: "",
    mentation: "BAR — Bright, Alert, Responsive",
    hydration: "Normal",
    pain: "",
    weight: "",
    bloodGlucose: "",
    pcvTp: "",
  });

  // Single object for Intake / Output / Nursing Check
  const [logForm, setLogForm] = useState(getInitLogForm);

  const timelineEntries = useMemo(() => {
    const list = Array.isArray(ioMonitoringList) ? ioMonitoringList : [];
    return list
      .map((item) => mapIoTimelineEntry(item, clinicVets, clinicDet, profile))
      .sort((a, b) => {
        const ta = moment(
          a.createdDate || a.createdAt || a.loggedAt || a.updatedDate,
        ).valueOf();
        const tb = moment(
          b.createdDate || b.createdAt || b.loggedAt || b.updatedDate,
        ).valueOf();
        return (tb || 0) - (ta || 0);
      });
  }, [ioMonitoringList, clinicVets, clinicDet, profile]);

  const refreshIoMonitoring = () => {
    if (appointmentId) {
      dispatch(getIOMonitoringByAppointmentId(appointmentId));
    }
  };

  useEffect(() => {
    refreshIoMonitoring();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const onChangeLogForm = (name, value) => {
    setLogForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (value) => {
    const category = getDefaultCategory(value);
    setLogForm((prev) => ({
      ...getInitLogForm(),
      type: value,
      category,
      finding: getDefaultFinding(category),
    }));
    setActiveModalField(null);
  };

  const handleCategoryChange = (value) => {
    setLogForm((prev) => ({
      ...prev,
      category: value,
      finding: getDefaultFinding(value) || prev.finding,
    }));
    setActiveModalField(null);
  };

  const handleRecordNoteClick = (formSetter) => {
    if (!activeModalField) {
      setShowNoFieldModal(true);
      return;
    }

    setIsListening(true);
    setTimeout(() => {
      const mockVoiceText = "Patient is stable and responding well.";
      formSetter((prev) => ({
        ...prev,
        [activeModalField]: prev[activeModalField]
          ? `${prev[activeModalField]} ${mockVoiceText}`
          : mockVoiceText,
      }));
      setIsListening(false);
    }, 1500);
  };

  const handleSaveVitals = () => {
    setVitals((prev) => ({
      ...prev,
      temperature: vitalsForm.temp ? `${vitalsForm.temp} °F` : prev.temperature,
      pulse: vitalsForm.pulse ? `${vitalsForm.pulse} bpm` : prev.pulse,
      respiration: vitalsForm.resp
        ? `${vitalsForm.resp} rpm`
        : prev.respiration,
      pain: vitalsForm.pain ? `${vitalsForm.pain}/24` : prev.pain,
      weight: vitalsForm.weight ? `${vitalsForm.weight} kg` : prev.weight,
      mentation: vitalsForm.mentation || prev.mentation,
      hydration: vitalsForm.hydration || prev.hydration,
      asOfTime:
        "as of " +
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
    }));
    setShowVitalsModal(false);
  };

  const closeLogModal = () => {
    setShowLogModal(false);
    setEditingEntry(null);
    setLogForm(getInitLogForm());
    setActiveModalField(null);
  };

  const onEditEntry = (item) => {
    if (!canManageIoEntry(item, profile, inpatientDoctorId)) return;
    if (!item?.ioMonitoringId) return;
    const original =
      (Array.isArray(ioMonitoringList) ? ioMonitoringList : [])?.find(
        (li) =>
          String(li?.ioMonitoringId ?? li?.id) === String(item?.ioMonitoringId),
      ) ?? item;
    setEditingEntry(original);
    setLogForm(mapIoItemToForm(original));
    setActiveModalField(null);
    setShowLogModal(true);
  };

  const onDeleteEntry = (item) => {
    if (!canManageIoEntry(item, profile, inpatientDoctorId)) return;
    if (!item?.ioMonitoringId) return;
    setSelectedEntry(item);
    setDelModVsble(true);
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
    setSelectedEntry(null);
  };

  const onConfirmDelete = () => {
    if (!appointmentId || !selectedEntry?.ioMonitoringId) return;
    dispatch(
      deleteIOMonitoringByAppointmentId({
        appointmentId,
        ioMonitoringId: selectedEntry.ioMonitoringId,
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseDelMod();
        refreshIoMonitoring();
      }
    });
  };

  const handleSaveLogEntry = () => {
    if (!appointmentId) return;

    const payload = buildIoRequestPayload(logForm);
    const ioMonitoringId =
      editingEntry?.ioMonitoringId ?? editingEntry?.id ?? null;

    const action = ioMonitoringId
      ? updateIOMonitoringByAppointmentId({
        appointmentId,
        ioMonitoringId,
        data: payload,
      })
      : createIOMonitoringByAppointmentId({
        appointmentId,
        data: payload,
      });

    dispatch(action).then((res) => {
      if (res?.payload) {
        refreshIoMonitoring();
        closeLogModal();
      }
    });
  };

  const renderText = (field, extra = {}) => (
    <div
      // Set active field without remounting inputs on each keystroke
      onMouseDown={() => {
        if (activeModalField !== field) setActiveModalField(field);
      }}
    >
      <CustomTextField
        fullWidth
        name={field}
        value={logForm[field] ?? ""}
        handleChange={(e) => onChangeLogForm(field, e?.target?.value)}
        {...extra}
      />
    </div>
  );

  const renderIntakeFields = () => {
    const { category } = logForm;

    if (category === "Oral Water") {
      return (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6}>
            <label className="modal-field-label">OFFERED (ML)</label>
            {renderText("offeredMl")}
          </Grid>
          <Grid item xs={12} sm={6}>
            <label className="modal-field-label">CONSUMED (ML)</label>
            {renderText("consumedMl")}
          </Grid>
        </Grid>
      );
    }

    if (category === "Food") {
      return (
        <>
          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">DIET / FOOD TYPE</label>
            {renderText("dietFoodType", {
              placeholder: "e.g. Prescription GI diet (wet)",
            })}
          </div>
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={6}>
              <label className="modal-field-label">OFFERED (G)</label>
              {renderText("offeredG")}
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="modal-field-label">CONSUMED (G / %)</label>
              {renderText("consumedG", { placeholder: "e.g. 80g or 75%" })}
            </Grid>
          </Grid>
        </>
      );
    }

    if (category === "Blood / Blood Products") {
      return (
        <>
          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">PRODUCT TYPE</label>
            <CustomSelect
              select
              list={bloodProductTypeOptions}
              value={logForm.productType}
              handleChange={(e) =>
                onChangeLogForm("productType", e?.target?.value)
              }
            />
          </div>
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={6}>
              <label className="modal-field-label">VOLUME (ML)</label>
              {renderText("volumeMl")}
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="modal-field-label">RATE (ML/HR)</label>
              {renderText("rate")}
            </Grid>
          </Grid>
        </>
      );
    }

    return (
      <>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6}>
            <label className="modal-field-label">FLUID TYPE</label>
            {renderText("fluidType", { placeholder: "e.g. LRS, NS 0.9%" })}
          </Grid>
          <Grid item xs={12} sm={6}>
            <label className="modal-field-label">RATE (ML/HR)</label>
            {renderText("rate")}
          </Grid>
        </Grid>
        <div style={{ marginBottom: 16 }}>
          <label className="modal-field-label">
            VOLUME INFUSED SO FAR (ML)
          </label>
          {renderText("volumeInfused")}
        </div>
      </>
    );
  };

  const renderOutputFields = () => {
    const { category } = logForm;

    if (category === "Vomiting") {
      return (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={4}>
            <label className="modal-field-label">EPISODES</label>
            {renderText("episodes")}
          </Grid>
          <Grid item xs={12} sm={8}>
            <label className="modal-field-label">CHARACTER</label>
            {renderText("character", {
              placeholder: "e.g. Bilious, undigested food",
            })}
          </Grid>
        </Grid>
      );
    }

    if (category === "Feces") {
      return (
        <>
          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">
              FECAL SCORE (PURINA 1-7 SCALE)
            </label>
            <CustomSelect
              select
              list={fecalScoreOptions}
              value={logForm.fecalScore}
              handleChange={(e) =>
                onChangeLogForm("fecalScore", e?.target?.value)
              }
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label className="modal-field-label">NOTES</label>
            {renderText("notes", { placeholder: "e.g. Blood-tinged" })}
          </div>
        </>
      );
    }

    if (category === "Blood Loss / Hemorrhage") {
      return (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6}>
            <label className="modal-field-label">
              ESTIMATED VOLUME (ML) / SEVERITY
            </label>
            {renderText("estimatedVolume", {
              placeholder: "e.g. 50 ml or Moderate",
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            <label className="modal-field-label">SOURCE / SITE</label>
            {renderText("sourceSite", {
              placeholder: "e.g. Surgical site, hematuria, epistaxis",
            })}
          </Grid>
        </Grid>
      );
    }

    if (category?.startsWith("Other")) {
      return (
        <div style={{ marginBottom: 16 }}>
          <label className="modal-field-label">DESCRIPTION</label>
          {renderText("description", {
            placeholder: "Describe the observation",
          })}
        </div>
      );
    }

    return (
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6}>
          <label className="modal-field-label">VOLUME (ML)</label>
          {renderText("volume")}
        </Grid>
        <Grid item xs={12} sm={6}>
          <label className="modal-field-label">COLOUR / CHARACTER</label>
          {renderText("colourCharacter", {
            placeholder: "e.g. Pale yellow, clear",
          })}
        </Grid>
      </Grid>
    );
  };

  const renderNursingFields = () => {
    const { category } = logForm;

    if (category === "Other Nursing Note") {
      return (
        <div style={{ marginBottom: 16 }}>
          <label className="modal-field-label">DESCRIPTION</label>
          {renderText("description", {
            placeholder: "Describe the observation",
          })}
        </div>
      );
    }

    if (category === "Pressure Area Check") {
      return (
        <div style={{ marginBottom: 16 }}>
          <label className="modal-field-label">FINDING</label>
          <CustomSelect
            select
            list={nursingFindingByCategory[category] || []}
            value={logForm.finding}
            handleChange={(e) => onChangeLogForm("finding", e?.target?.value)}
          />
        </div>
      );
    }

    const findingList = nursingFindingByCategory[category] || [];
    return (
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6}>
          <label className="modal-field-label">SITE</label>
          {renderText("site", {
            placeholder:
              category === "Wound / Incision Check"
                ? "e.g. Ventral midline"
                : "e.g. Left cephalic",
          })}
        </Grid>
        <Grid item xs={12} sm={6}>
          <label className="modal-field-label">FINDING</label>
          <CustomSelect
            select
            list={findingList}
            value={logForm.finding}
            handleChange={(e) => onChangeLogForm("finding", e?.target?.value)}
          />
        </Grid>
      </Grid>
    );
  };

  const renderTypeFields = () => {
    if (logForm.type === "Output") return renderOutputFields();
    if (logForm.type === "Nursing Check") return renderNursingFields();
    return renderIntakeFields();
  };

  // const modalTitle =
  //   logForm.type === "Output"
  //     ? "Log output"
  //     : logForm.type === "Nursing Check"
  //       ? "Nursing check"
  //       : "Log intake";

  return (
    <div className="ph20 w100Per">
      {/* <div className="io-card-container"> */}
      {/* <div className="io-header-row">
        <h2 className="io-section-title">Vitals — latest & trend</h2>
        <div>
          <CustomButton
            text="+ Log vitals"
            smallBtn
            onClick={() => {
              setActiveModalField(null);
              setShowVitalsModal(true);
            }}
          />
        </div>
      </div>

      <div className="vitals-grid-row">
        <div className="vital-card">
          <div className="vital-label">TEMP</div>
          <div className="vital-value temp-red">{vitals.temperature}</div>
          <div className="vital-asof">{vitals.asOfTime}</div>
          <svg
            className="vital-sparkline"
            viewBox="0 0 100 20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,5 L50,12 L100,18"
              fill="none"
              stroke="#DC2626"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="vital-card">
          <div className="vital-label">PULSE</div>
          <div className="vital-value pulse-blue">{vitals.pulse}</div>
          <div className="vital-asof">{vitals.asOfTime}</div>
          <svg
            className="vital-sparkline"
            viewBox="0 0 100 20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,5 L50,12 L100,18"
              fill="none"
              stroke="#2563EB"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="vital-card">
          <div className="vital-label">RESP</div>
          <div className="vital-value resp-green">{vitals.respiration}</div>
          <div className="vital-asof">{vitals.asOfTime}</div>
          <svg
            className="vital-sparkline"
            viewBox="0 0 100 20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,5 L50,12 L100,18"
              fill="none"
              stroke="#16A34A"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="vital-card">
          <div className="vital-label">PAIN (CMPS-SF)</div>
          <div className="vital-value pain-orange">{vitals.pain}</div>
          <div className="vital-asof">{vitals.asOfTime}</div>
          <svg
            className="vital-sparkline"
            viewBox="0 0 100 20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,5 L50,15 L100,15"
              fill="none"
              stroke="#D97706"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="vital-card">
          <div className="vital-label">WEIGHT</div>
          <div className="vital-value weight-purple">{vitals.weight}</div>
          <div className="vital-asof">{vitals.asOfTime}</div>
          <svg
            className="vital-sparkline"
            viewBox="0 0 100 20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,5 L50,12 L100,18"
              fill="none"
              stroke="#9333EA"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="vital-card">
          <div className="vital-label">MENTATION</div>
          <div className="vital-value navy-text">{vitals.mentation}</div>
        </div>

        <div className="vital-card">
          <div className="vital-label">HYDRATION</div>
          <div className="vital-value navy-text" style={{ fontSize: 15 }}>
            {vitals.hydration}
          </div>
        </div>
      </div> */}

      <div className="timeline-section">
        <div className="io-header-row">
          <h2 className="io-section-title">Intake, Output & Nursing Checks</h2>
          <div className="io-action-group">
            <CustomButton
              text="+ Add log"
              smallBtn
              whiteBtn
              onClick={() => {
                setEditingEntry(null);
                setActiveModalField(null);
                setLogForm(getInitLogForm());
                setShowLogModal(true);
              }}
            />
          </div>
        </div>

        <div className="timeline-list">
          {timelineEntries.map((item) => (
            <div key={item.id} className="timeline-item">
              <div className="io-timeline-time">{item.time}</div>
              <div className="timeline-content">
                <div className="timeline-content-header">
                  <span
                    className={
                      item.badgeType === "warning"
                        ? "badge-warning"
                        : item.badgeType === "info"
                          ? "badge-info"
                          : "badge-purple"
                    }
                  >
                    {item.category}
                  </span>
                  {canManageIoEntry(item, profile, inpatientDoctorId) ? (
                    <div className="timeline-actions">
                      <div className="cursor" onClick={() => onEditEntry(item)}>
                        <div className="flex-center">
                          <EditIcon sx={edtBtn} />
                        </div>
                        <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                          Edit
                        </Typography>
                      </div>
                      <div
                        className="cursor ml15"
                        onClick={() => onDeleteEntry(item)}
                      >
                        <div className="flex-center">
                          <DeleteOutlineIcon sx={delBtn} />
                        </div>
                        <Typography className="red3 fs8 txt-semi-bold text-align-center">
                          Delete
                        </Typography>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="timeline-desc">{item.description}</div>
                <div className="timeline-by">
                  {item.loggedBy} on{" "}
                  {moment(item?.createdDate ?? "").format("DD MMM YYYY")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* </div> */}

      {/* Log Vitals (unchanged) */}
      <CustomModal
        open={showVitalsModal}
        onClose={() => setShowVitalsModal(false)}
        modal
        headerShown
        headerCenter={false}
        header="Log vitals"
        modalWidth={45}
      >
        <div style={{ padding: "8px 0" }}>
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={4}>
              <label className="modal-field-label">TEMP (°F)</label>
              <input
                type="text"
                className={`modal-input ${activeModalField === "temp" ? "field-active" : ""}`}
                value={vitalsForm.temp}
                onFocus={() => setActiveModalField("temp")}
                onClick={() => setActiveModalField("temp")}
                onChange={(e) =>
                  setVitalsForm((prev) => ({ ...prev, temp: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <label className="modal-field-label">PULSE (BPM)</label>
              <input
                type="text"
                className={`modal-input ${activeModalField === "pulse" ? "field-active" : ""}`}
                value={vitalsForm.pulse}
                onFocus={() => setActiveModalField("pulse")}
                onClick={() => setActiveModalField("pulse")}
                onChange={(e) =>
                  setVitalsForm((prev) => ({ ...prev, pulse: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <label className="modal-field-label">RESP (RPM)</label>
              <input
                type="text"
                className={`modal-input ${activeModalField === "resp" ? "field-active" : ""}`}
                value={vitalsForm.resp}
                onFocus={() => setActiveModalField("resp")}
                onClick={() => setActiveModalField("resp")}
                onChange={(e) =>
                  setVitalsForm((prev) => ({ ...prev, resp: e.target.value }))
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={6}>
              <label className="modal-field-label">MENTATION</label>
              <select
                className="modal-select"
                value={vitalsForm.mentation}
                onChange={(e) =>
                  setVitalsForm((prev) => ({
                    ...prev,
                    mentation: e.target.value,
                  }))
                }
              >
                {mentationOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="modal-field-label">HYDRATION</label>
              <select
                className="modal-select"
                value={vitalsForm.hydration}
                onChange={(e) =>
                  setVitalsForm((prev) => ({
                    ...prev,
                    hydration: e.target.value,
                  }))
                }
              >
                {hydrationOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Grid>
          </Grid>

          <div style={{ marginBottom: 16 }}>
            <label className="modal-field-label">
              PAIN SCORE — GLASGOW CMPS-SF{" "}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: "#6B7280",
                  textTransform: "none",
                }}
              >
                (0–24 ambulatory / 0–20 non-ambulatory; ≥6 usually triggers
                analgesia review)
              </span>
            </label>
            <input
              type="text"
              className={`modal-input ${activeModalField === "pain" ? "field-active" : ""}`}
              value={vitalsForm.pain}
              onFocus={() => setActiveModalField("pain")}
              onClick={() => setActiveModalField("pain")}
              onChange={(e) =>
                setVitalsForm((prev) => ({ ...prev, pain: e.target.value }))
              }
            />
          </div>

          <div className="modal-section-heading">OPTIONAL SPOT CHECKS</div>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <label className="modal-field-label">WEIGHT (KG)</label>
              <input
                type="text"
                className={`modal-input ${activeModalField === "weight" ? "field-active" : ""}`}
                value={vitalsForm.weight}
                onFocus={() => setActiveModalField("weight")}
                onClick={() => setActiveModalField("weight")}
                onChange={(e) =>
                  setVitalsForm((prev) => ({
                    ...prev,
                    weight: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <label className="modal-field-label">BLOOD GLUCOSE (MG/DL)</label>
              <input
                type="text"
                className={`modal-input ${activeModalField === "bloodGlucose" ? "field-active" : ""}`}
                value={vitalsForm.bloodGlucose}
                onFocus={() => setActiveModalField("bloodGlucose")}
                onClick={() => setActiveModalField("bloodGlucose")}
                onChange={(e) =>
                  setVitalsForm((prev) => ({
                    ...prev,
                    bloodGlucose: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <label className="modal-field-label">PCV / TP</label>
              <input
                type="text"
                className={`modal-input ${activeModalField === "pcvTp" ? "field-active" : ""}`}
                placeholder="e.g. 42% / 7.2 g/dL"
                value={vitalsForm.pcvTp}
                onFocus={() => setActiveModalField("pcvTp")}
                onClick={() => setActiveModalField("pcvTp")}
                onChange={(e) =>
                  setVitalsForm((prev) => ({ ...prev, pcvTp: e.target.value }))
                }
              />
            </Grid>
          </Grid>

          <div className="modal-footer-actions">
            <div>
              <CustomButton
                text="Cancel"
                redBtn
                smallBtn
                onClick={() => setShowVitalsModal(false)}
              />
            </div>
            <div>
              <CustomButton text="Save" smallBtn onClick={handleSaveVitals} />
            </div>
          </div>
        </div>
      </CustomModal>

      {/* Unified Intake / Output / Nursing Check modal */}
      <CustomModal
        open={showLogModal}
        onClose={closeLogModal}
        modal
        headerShown
        headerCenter={false}
        header={editingEntry ? "Edit Log" : "Add Log"}
        modalWidth={45}
      >
        <div style={{ padding: "8px 0" }}>
          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">TYPE</label>
            <CustomSelect
              select
              list={logTypeOptions}
              value={logForm.type}
              handleChange={(e) => handleTypeChange(e?.target?.value)}
            />
          </div>

          <div style={{ marginBottom: 6 }}>
            <label className="modal-field-label">CATEGORY</label>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <CustomSelect
                  select
                  list={getCategoryOptions(logForm.type)}
                  value={logForm.category}
                  handleChange={(e) => handleCategoryChange(e?.target?.value)}
                />
              </Grid>
            </Grid>
          </div>

          {renderTypeFields()}

          <div className="modal-footer-actions">
            <div>
              <CustomButton
                text="Cancel"
                redBtn
                smallBtn
                onClick={closeLogModal}
              />
            </div>
            <div>
              <CustomButton
                text={editingEntry ? "Update" : "Log entry"}
                smallBtn
                onClick={handleSaveLogEntry}
              />
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={delModVsble}
        onClose={onCloseDelMod}
        header="Delete"
        modal
        modalWidth={40}
      >
        <p
          style={{
            fontFamily: "Montserrat",
            fontSize: 16,
            margin: "0 0 0 10px",
          }}
        >
          Are you sure you want to delete this I/O log entry?
        </p>
        <div className="flex-center mv20">
          <div className="mr10">
            <CustomButton
              text="Yes"
              redBtn
              onClick={onConfirmDelete}
              smallBtn
            />
          </div>
          <div className="ml10">
            <CustomButton text="No" onClick={onCloseDelMod} smallBtn />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={showNoFieldModal}
        onClose={() => setShowNoFieldModal(false)}
        modal
        headerShown
        headerCenter
        header="This page says"
        modalWidth={35}
      >
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <p
            style={{
              fontFamily: "Montserrat",
              fontSize: 14,
              color: "#333",
              marginBottom: 20,
            }}
          >
            Click into any text field below, then press Record Note.
          </p>
          <CustomButton
            text="OK"
            smallBtn
            onClick={() => setShowNoFieldModal(false)}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default IOMonitoring;
